import React, { useEffect, useMemo, useState } from "react";
import {
  deleteMessageById,
  getMessagesByUser,
  markMessageAsRead,
  sendMessage,
} from "../../../services/messageServices";
import { getAdminsIds } from "../../../services/userServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import { useUser } from "../../Home/UserContext";
import { useMessage } from "../../../hooks/MessageContext";
import useScrollToTop from "../../../hooks/useScollToTop";
import DOMPurify from "dompurify";
import {
  FaCheckCircle,
  FaEnvelopeOpenText,
  FaPaperPlane,
  FaReply,
  FaSearch,
  FaSyncAlt,
  FaTrash,
  FaUserCircle,
  FaTimes,
  FaInbox,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function formatDate(dateString) {
  if (!dateString) return "";
  try {
    return new Date(dateString).toLocaleString("fr-FR", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "";
  }
}

function stripHtml(html = "") {
  return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}

function getSenderLabel(message) {
  if (message?.Sender?.prenom || message?.Sender?.nom) {
    return `${message.Sender?.prenom || ""} ${message.Sender?.nom || ""}`.trim();
  }
  return "Administrateur";
}

function getPreviewText(html = "", maxLength = 220) {
  const text = stripHtml(html || "");
  if (!text) return { text: "Message vide", truncated: false };
  if (text.length <= maxLength) return { text, truncated: false };

  return {
    text: `${text.slice(0, maxLength).trimEnd()}…`,
    truncated: true,
  };
}

function ComposerModal({
  open,
  title,
  subject,
  content,
  setSubject,
  setContent,
  onClose,
  onSubmit,
  isSending,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl overflow-hidden rounded-[24px] border border-white/10 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div>
            <h3 className="text-xl font-black text-slate-900 dark:text-slate-100">
              {title}
            </h3>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Rédigez votre message puis envoyez-le.
            </p>
          </div>

          <button
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-5">
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                Sujet
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Sujet du message"
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                Message
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Votre message..."
                className="min-h-[180px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
              />
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              onClick={onSubmit}
              disabled={isSending}
              className={cn(
                "inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-semibold text-white transition",
                isSending
                  ? "cursor-not-allowed bg-slate-400"
                  : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
              )}
            >
              <FaPaperPlane />
              {isSending ? "Envoi..." : "Envoyer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompactActionButton({
  icon,
  label,
  onClick,
  disabled = false,
  variant = "default",
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-xl px-2.5 py-2 text-xs font-semibold transition",
        disabled && "cursor-not-allowed opacity-60",
        variant === "danger" &&
          "bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20",
        variant === "primary" &&
          "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600",
        variant === "default" &&
          "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
      )}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

const MessagerieJudoka = () => {
  useScrollToTop();

  const { addNotification } = useNotification();
  const { user } = useUser();
  const { unreadMessageCount, setUnreadMessageCount } = useMessage();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [isBulkLoading, setIsBulkLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState([]);

  const [composerOpen, setComposerOpen] = useState(false);
  const [composerTitle, setComposerTitle] = useState("Nouveau message");
  const [composerRecipients, setComposerRecipients] = useState([]);
  const [messageSubject, setMessageSubject] = useState("");
  const [messageContent, setMessageContent] = useState("");
  const [isSending, setIsSending] = useState(false);

  const syncMessagesAndUnread = (nextMessages) => {
    setMessages(nextMessages);
    const unreadCount = nextMessages.filter((msg) => !msg.read_message).length;
    setUnreadMessageCount(unreadCount);
  };

  const fetchMessages = async () => {
    if (!user?.userId) {
      addNotification("Erreur : utilisateur non trouvé.", "error");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const messagesData = await getMessagesByUser(user.userId);
      const normalized = Array.isArray(messagesData) ? messagesData : [];
      syncMessagesAndUnread(normalized);
    } catch (error) {
      console.error("Erreur lors de la récupération des messages :", error);
      addNotification("Erreur lors de la récupération des messages.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [user]);

  const filteredMessages = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return [...messages]
      .filter((message) => {
        if (activeFilter === "unread" && message.read_message) return false;
        if (activeFilter === "read" && !message.read_message) return false;

        if (!term) return true;

        const sender = getSenderLabel(message).toLowerCase();
        const subject = (message.subject || "").toLowerCase();
        const content = stripHtml(message.content || "").toLowerCase();

        return (
          sender.includes(term) ||
          subject.includes(term) ||
          content.includes(term)
        );
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [messages, searchTerm, activeFilter]);

  const readCount = useMemo(
    () => messages.filter((msg) => msg.read_message).length,
    [messages]
  );

  const setMessageReadStateLocally = (messageId, readState) => {
    const nextMessages = messages.map((message) =>
      message.id === messageId
        ? { ...message, read_message: readState }
        : message
    );
    syncMessagesAndUnread(nextMessages);
  };

  const handleExpandToggle = async (message) => {
    const isExpanded = expandedIds.includes(message.id);
    const nextExpanded = isExpanded
      ? expandedIds.filter((id) => id !== message.id)
      : [...expandedIds, message.id];

    setExpandedIds(nextExpanded);

    if (!isExpanded && !message.read_message) {
      try {
        await markMessageAsRead(message.id);
        setMessageReadStateLocally(message.id, true);
      } catch (error) {
        console.error("Erreur lors du marquage comme lu :", error);
      }
    }
  };

  const toggleReadStatus = async (messageId) => {
    const targetMessage = messages.find((m) => m.id === messageId);
    if (!targetMessage) return;

    try {
      setActionLoadingId(messageId);
      await markMessageAsRead(messageId);
      setMessageReadStateLocally(messageId, !targetMessage.read_message);

      addNotification(
        !targetMessage.read_message
          ? "Message marqué comme lu."
          : "Message marqué comme non lu.",
        "success"
      );
    } catch (error) {
      console.error("Erreur lors du basculement de l'état de lecture :", error);
      addNotification(
        "Erreur lors du basculement de l'état de lecture.",
        "error"
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    const confirmed = window.confirm(
      "Voulez-vous vraiment supprimer ce message ?"
    );
    if (!confirmed) return;

    try {
      setActionLoadingId(messageId);
      await deleteMessageById(messageId);

      const nextMessages = messages.filter((message) => message.id !== messageId);
      syncMessagesAndUnread(nextMessages);
      setExpandedIds((prev) => prev.filter((id) => id !== messageId));

      addNotification("Message supprimé avec succès.", "success");
    } catch (error) {
      console.error("Erreur lors de la suppression du message :", error);
      addNotification("Erreur lors de la suppression du message.", "error");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleMarkAllAsRead = async () => {
    const unreadMessages = messages.filter((msg) => !msg.read_message);
    if (!unreadMessages.length) return;

    try {
      setIsBulkLoading(true);
      await Promise.all(unreadMessages.map((msg) => markMessageAsRead(msg.id)));

      const nextMessages = messages.map((msg) => ({
        ...msg,
        read_message: true,
      }));
      syncMessagesAndUnread(nextMessages);

      addNotification("Tous les messages ont été marqués comme lus.", "success");
    } catch (error) {
      console.error("Erreur lors du marquage global :", error);
      addNotification("Erreur lors du marquage des messages.", "error");
    } finally {
      setIsBulkLoading(false);
    }
  };

  const openNewAdminMessage = async () => {
    try {
      const adminsResponse = await getAdminsIds();

      let recipientIds = [];

      if (Array.isArray(adminsResponse)) {
        recipientIds = adminsResponse
          .map((item) =>
            typeof item === "object" ? item.id || item.userId : item
          )
          .filter(Boolean);
      }

      if (!recipientIds.length) recipientIds = [1];

      setComposerTitle("Message à l'administrateur");
      setComposerRecipients(recipientIds);
      setMessageSubject("");
      setMessageContent("");
      setComposerOpen(true);
    } catch (error) {
      console.error("Erreur récupération admins :", error);
      setComposerTitle("Message à l'administrateur");
      setComposerRecipients([1]);
      setMessageSubject("");
      setMessageContent("");
      setComposerOpen(true);
    }
  };

  const openReplyModal = (message) => {
    setComposerTitle(`Répondre à ${getSenderLabel(message)}`);
    setComposerRecipients([message.sender_id]);
    setMessageSubject(
      message.subject?.startsWith("Re:")
        ? message.subject
        : `Re: ${message.subject || ""}`
    );
    setMessageContent("");
    setComposerOpen(true);
  };

  const closeComposer = () => {
    setComposerOpen(false);
    setComposerRecipients([]);
    setMessageSubject("");
    setMessageContent("");
  };

  const handleSendMessage = async () => {
    if (!user?.userId) {
      addNotification("Utilisateur non connecté.", "error");
      return;
    }

    if (!messageSubject.trim() || !messageContent.trim()) {
      addNotification("Le sujet et le contenu du message sont requis.", "error");
      return;
    }

    if (!composerRecipients.length) {
      addNotification("Aucun destinataire trouvé.", "error");
      return;
    }

    try {
      setIsSending(true);

      await Promise.all(
        composerRecipients.map((recipientId) =>
          sendMessage({
            sender_id: user.userId,
            recipient_id: recipientId,
            subject: messageSubject.trim(),
            content: messageContent.trim(),
          })
        )
      );

      addNotification("Message envoyé avec succès.", "success");
      closeComposer();
    } catch (error) {
      console.error("Erreur lors de l'envoi du message :", error);
      addNotification("Erreur lors de l'envoi du message.", "error");
    } finally {
      setIsSending(false);
    }
  };

  const filterButtons = [
    { id: "all", label: "Tous", count: messages.length },
    { id: "unread", label: "Non lus", count: unreadMessageCount },
    { id: "read", label: "Lus", count: readCount },
  ];

  return (
    <div className="w-full bg-slate-50 pb-12 pt-[100px] dark:bg-slate-950">
      <div className="mx-auto max-w-[1100px] px-4 sm:px-6">
        <section className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="mt-4 text-3xl font-black text-slate-900 dark:text-slate-100">
              Messagerie
            </h1>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Consultez vos messages et échangez avec l’administrateur.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={fetchMessages}
              disabled={loading}
              className={cn(
                "inline-flex items-center justify-center gap-2 rounded-2xl border px-4 py-2.5 text-sm font-semibold transition",
                loading
                  ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500"
                  : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
              )}
            >
              <FaSyncAlt />
              {loading ? "Chargement..." : "Rafraîchir"}
            </button>

            <button
              onClick={handleMarkAllAsRead}
              disabled={!unreadMessageCount || isBulkLoading}
              className={cn(
                "inline-flex items-center justify-center gap-2 rounded-2xl border px-4 py-2.5 text-sm font-semibold transition",
                !unreadMessageCount || isBulkLoading
                  ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500"
                  : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
              )}
            >
              <FaCheckCircle />
              {isBulkLoading ? "Traitement..." : "Tout marquer comme lu"}
            </button>
          </div>
        </section>

        <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="relative w-full xl:max-w-xl">
              <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              <input
                type="text"
                placeholder="Rechercher par expéditeur, sujet ou contenu..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {filterButtons.map((filter) => {
                const active = activeFilter === filter.id;

                return (
                  <button
                    key={filter.id}
                    onClick={() => setActiveFilter(filter.id)}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition",
                      active
                        ? "bg-blue-600 text-white dark:bg-blue-500"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                    )}
                  >
                    {filter.label}
                    <span
                      className={cn(
                        "inline-flex min-w-[22px] items-center justify-center rounded-full px-2 py-0.5 text-xs",
                        active
                          ? "bg-white/15 text-white"
                          : "bg-white text-slate-600 dark:bg-slate-700 dark:text-slate-200"
                      )}
                    >
                      {filter.count}
                    </span>
                  </button>
                );
              })}

              <button
                onClick={openNewAdminMessage}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                <FaPaperPlane />
                Message à l'administrateur
              </button>
            </div>
          </div>
        </section>

        <section className="mt-5">
          <div className="mb-3 px-1">
            <h2 className="text-xl font-black text-slate-900 dark:text-slate-100">
              Messages reçus
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {filteredMessages.length} message(s) affiché(s)
            </p>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="animate-pulse rounded-[22px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-12 w-12 rounded-full bg-slate-200 dark:bg-slate-700" />
                    <div className="flex-1 space-y-3">
                      <div className="h-4 w-40 rounded bg-slate-200 dark:bg-slate-700" />
                      <div className="h-3 w-56 rounded bg-slate-200 dark:bg-slate-700" />
                      <div className="h-16 rounded-2xl bg-slate-100 dark:bg-slate-800" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredMessages.length > 0 ? (
            <div className="space-y-4">
              {filteredMessages.map((message) => {
                const senderName = getSenderLabel(message);
                const isExpanded = expandedIds.includes(message.id);
                const { text: previewText, truncated } = getPreviewText(
                  message.content,
                  220
                );
                const isBusy = actionLoadingId === message.id;

                return (
                  <article
                    key={message.id}
                    className={cn(
                      "rounded-[22px] border bg-white p-4 shadow-sm transition dark:bg-slate-900",
                      !message.read_message
                        ? "border-blue-200 dark:border-blue-500/20"
                        : "border-slate-200 dark:border-slate-800"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      {message.Sender?.photoURL ? (
                        <img
                          src={message.Sender.photoURL}
                          alt={senderName}
                          className="h-12 w-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                          <FaUserCircle className="text-2xl" />
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="truncate text-sm font-bold text-slate-900 dark:text-slate-100">
                                {senderName}
                              </p>

                              {!message.read_message && (
                                <span className="inline-flex h-2.5 w-2.5 shrink-0 rounded-full bg-blue-600 dark:bg-blue-400" />
                              )}

                              <span
                                className={cn(
                                  "inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide",
                                  message.read_message
                                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                                    : "bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300"
                                )}
                              >
                                {message.read_message ? "Lu" : "Non lu"}
                              </span>
                            </div>

                            <h3 className="mt-1 text-base font-black text-slate-900 dark:text-slate-100">
                              {message.subject || "Sans sujet"}
                            </h3>

                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-500">
                              {formatDate(message.created_at)}
                            </p>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <CompactActionButton
                              icon={<FaCheckCircle />}
                              label={
                                message.read_message ? "Non lu" : "Marquer lu"
                              }
                              onClick={() => toggleReadStatus(message.id)}
                              disabled={isBusy}
                            />

                            <CompactActionButton
                              icon={<FaReply />}
                              label="Répondre"
                              onClick={() => openReplyModal(message)}
                              variant="primary"
                            />

                            <CompactActionButton
                              icon={<FaTrash />}
                              label="Supprimer"
                              onClick={() => handleDeleteMessage(message.id)}
                              disabled={isBusy}
                              variant="danger"
                            />
                          </div>
                        </div>

                        <div className="mt-3 rounded-[18px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
                          {isExpanded || !truncated ? (
                            <div
                              className="text-sm leading-7 text-slate-700 dark:text-slate-200"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                  message.content || "<p>Message vide</p>"
                                ),
                              }}
                            />
                          ) : (
                            <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                              {previewText}
                            </p>
                          )}

                          {truncated && (
                            <div className="mt-3">
                              <button
                                onClick={() => handleExpandToggle(message)}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 transition hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                {isExpanded ? (
                                  <>
                                    <FaChevronUp />
                                    Réduire
                                  </>
                                ) : (
                                  <>
                                    <FaChevronDown />
                                    Voir la suite
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="flex min-h-[260px] flex-col items-center justify-center rounded-[24px] border border-slate-200 bg-white px-6 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                <FaInbox className="text-xl" />
              </div>
              <h3 className="mt-4 text-lg font-black text-slate-900 dark:text-slate-100">
                Aucun message
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                Aucun message ne correspond à votre filtre actuel.
              </p>
            </div>
          )}
        </section>
      </div>

      <ComposerModal
        open={composerOpen}
        title={composerTitle}
        subject={messageSubject}
        content={messageContent}
        setSubject={setMessageSubject}
        setContent={setMessageContent}
        onClose={closeComposer}
        onSubmit={handleSendMessage}
        isSending={isSending}
      />
    </div>
  );
};

export default MessagerieJudoka;